import { useEffect, useState } from "react";
import { useDND } from "../../contexts/DNDContext";
import { useSettings } from "../../contexts/SettingsContext";
import { useToast } from "../../contexts/ToastContext";
import { useRiskFlowAlertSurfaceActive } from "../../lib/riskflow-alert-surface";
import { shouldDeliverRiskFlowHeadlineCard } from "../../lib/riskflow-notification-policy";
import { isElectron } from "../../lib/platform";
import {
  RiskFlowNotificationCard,
  type Notification,
} from "../NotificationToast";
import {
  AnimatedToastStack,
  type AnimatedToast,
} from "../motion/animated-toast-stack";
import {
  RISKFLOW_CATEGORIES,
  notificationFromDetail,
} from "./notification-container-model";
import { FadingDivider } from "../shared/FadingDivider";
import "./s156-cardless-notifications.css";

const MAX_VISIBLE_ALERTS = 3;

export function RiskFlowAlertPresenter() {
  const [alerts, setAlerts] = useState<Notification[]>([]);
  const { preferences } = useSettings();
  const { dndActive } = useDND();
  const { isNotificationBlocked } = useToast();
  const isTradingSurfaceActive = useRiskFlowAlertSurfaceActive();

  useEffect(() => {
    if (!isTradingSurfaceActive) setAlerts([]);
  }, [isTradingSurfaceActive]);

  useEffect(() => {
    const receive = (event: Event) => {
      const detail = (event as CustomEvent<Partial<Notification>>).detail;
      if (!detail || dndActive || !isTradingSurfaceActive) return;
      const alert = notificationFromDetail(detail);
      if (!RISKFLOW_CATEGORIES.has(alert.category)) return;
      const channelEnabled = isElectron()
        ? preferences.notifications.deliveryChannels.desktop
        : preferences.notifications.deliveryChannels.web;
      if (
        !shouldDeliverRiskFlowHeadlineCard(
          { ivScore: alert.ivScore },
          isTradingSurfaceActive,
        ) ||
        !channelEnabled ||
        isNotificationBlocked("riskflow-watch")
      ) {
        return;
      }
      setAlerts((current) => {
        const key = alert.eventId ?? alert.id;
        return [
          alert,
          ...current.filter((item) => (item.eventId ?? item.id) !== key),
        ].slice(0, MAX_VISIBLE_ALERTS);
      });
    };
    window.addEventListener("fintheon:riskflow-card-notification", receive);
    return () =>
      window.removeEventListener(
        "fintheon:riskflow-card-notification",
        receive,
      );
  }, [dndActive, isNotificationBlocked, isTradingSurfaceActive, preferences]);

  if (alerts.length === 0) return null;
  const toasts: AnimatedToast[] = alerts.map((alert) => ({
    id: alert.id,
    title: alert.title,
    createdAt: alert.timestamp.getTime(),
    duration: 0,
  }));
  const orderedToastIds = [...toasts]
    .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))
    .map((toast) => toast.id);
  const showCloseAll = alerts.length > 1;

  return (
    <div data-riskflow-alert-presenter>
      <AnimatedToastStack
        toasts={toasts}
        onDismiss={(id) =>
          setAlerts((current) => current.filter((item) => item.id !== id))
        }
        position="bottom-right"
        placement="fixed"
        maxVisible={MAX_VISIBLE_ALERTS}
        newestFirst
        stacked={showCloseAll}
        className="s156-notification-stack !bottom-[56px] !right-[18px] !w-[min(620px,calc(100vw-32px))] !max-w-[620px] items-end"
        classNames={{
          root: "riskflow-alert-stack s156-notification-stack",
          item: "s156-notification-stack-item",
          surface:
            "!overflow-visible !rounded-none !border-0 !bg-transparent !p-0 !shadow-none !backdrop-blur-none",
        }}
        renderToast={(toast) => {
          const alert = alerts.find((item) => item.id === toast.id);
          if (!alert) return null;
          const alertIndex = orderedToastIds.indexOf(alert.id);
          return (
            <>
              <RiskFlowNotificationCard
                notification={alert}
                onDismiss={(id) =>
                  setAlerts((current) =>
                    current.filter((item) => item.id !== id),
                  )
                }
                onDismissAll={
                  showCloseAll && alert.id === alerts[0]?.id
                    ? () => setAlerts([])
                    : undefined
                }
              />
              {alertIndex < orderedToastIds.length - 1 ? (
                <FadingDivider
                  inset={12}
                  className="s156-notification-divider"
                />
              ) : null}
            </>
          );
        }}
      />
    </div>
  );
}
